import { Box, Button, Divider, Flex, Grid, HStack, IconButton, Menu, MenuButton, MenuItem, MenuList, Text, Tooltip, VStack } from "@chakra-ui/react";
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import apiClient from "../../clients/apiClient";
import { ChevronRight, DomainOutlined, FolderOpenOutlined, GroupOutlined, MoreHoriz } from "@mui/icons-material";
import FileIcon from "../../components/FileIcon";
import { DownloadFile2 } from "../../components/DownloadFile";
import ControlArrows from '../../utils/ControlArrows';
import FileLibraryTopButton from "./FileLibraryTopButton";
import CreateFolderModal from "./CreateFolderModal";
import UploadFileModal from "./UploadFileModal";
import FileGroupsModal from "./FileGroupsModal";
import LibraryAgentImage from './LibraryAgent.png';

const FileLibrary = ({viewType, isLibraryOpen, onClose, onOpen}) => {
    const {folderId} = useParams();
    const [finalFolderId, setFinalFolderId] = React.useState(folderId);
    const [requiresSetup, setRequiresSetup] = React.useState(false);
    const [folders, setFolders] = React.useState([]);
    const [files, setFiles] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [isButtonDisabled, setIsButtonDisabled] = React.useState(false);
    const [groups, setGroups] = React.useState([]);

    const [timezone, setTimezone] = React.useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const handleTimezoneChange = (event) => {
        setTimezone(event.target.value);
    };


    const navigate = useNavigate();

    React.useEffect(() => {
        setLoading(true);
        let getFolder = {}
        if (folderId === undefined) {
            getFolder = {
                get_root_folder: true,
            }
        } else {
            let folderName = "";
            const folderStructure = JSON.parse(sessionStorage.getItem("folderStructure")) || [];
            const folder = folderStructure.find((folder) => folder.id === folderId);
            if (folder) {
                folderName = folder.name;
            }
            getFolder = {
                folder_id: folderId,
                folder_name: folderName,
            }
        }
            
                
        apiClient.post('/library/library-actions', {
            action: "get_folder",
            get_folder: getFolder,
        }).then(({data}) => {
            if (data.get_folder?.requires_setup) {
                setRequiresSetup(true);
            } else {
                setFolders(data.get_folder.folders);
                setFiles(data.get_folder.files);
                setFinalFolderId(data.get_folder.current_folder_id);
                for (let i = 0; i < data.get_folder.folders.length; i++) {
                    addFolderToSession(data.get_folder.current_folder_id, data.get_folder.folders[i]);
                }
            }
            if (folderId === undefined) {
                setIsButtonDisabled(true)
            }
            setLoading(false);
        })
    }, [folderId])

    const getGroups = () => {
        apiClient.get('/account-settings/groups?get_private_groups=true').then(({data}) => {
            console.log("[FileLibrary] get_groups data is ", data);
            const groups = data.group;
            setGroups(groups);
        }) 
    }

    const setupLibrary = () => {
        setLoading(true);
        apiClient.post('/library/library-actions', {
            action: "setup",
        }).then(({data}) => {
            if (data.success) {
                setRequiresSetup(false);
                window.location.reload();
            }
            console.log("[FileLibrary] Setup Library Response", data);
            setLoading(false);
        })
    }

    const getFolderBreadcrumbs = () => {
        const folderStructure = JSON.parse(sessionStorage.getItem("folderStructure")) || [];
        let breadcrumbs = [];
        let currentFolder = folderStructure.find((folder) => folder.id === finalFolderId);

        while (currentFolder) {
            breadcrumbs.unshift(currentFolder);
            currentFolder = folderStructure.find((folder) => folder.id === currentFolder.parent_id);
        }

        breadcrumbs.unshift({
            id: undefined,
            name: "Library",
        })

        const buildSingleBreadcrumb = (folder, index) => {
            return (
                <Text 
                    key={index}
                    _hover={{
                        cursor: "pointer"
                    }}
                    onClick={() => openFolder(folder.id)}
                    whiteSpace="nowrap" 
                    overflow="hidden" 
                    textOverflow="ellipsis"
                >
                    {folder.name}
                </Text>
            )
        }
        const buildBreadcrumbSeparator = () => {
            return (
                <IconButton
                    sx={{padding:0, bgColor: "transparent", "&:hover":{backgroundColor: "transparent", cursor: "default"}}}
                >
                <ChevronRight
                    sx={{ padding:0, fontSize: 22, color: "#5d5d5d", bgColor: "transparent", cursor: "default" }}
                />
                </IconButton>
            )
        }

        if (breadcrumbs.length > 4) {
            return (
                <HStack fontSize="16px" fontWeight="bold" spacing={0} maxW={"50%"} overflowX={"hidden"} maxH={"40px"}>
                    {buildSingleBreadcrumb(breadcrumbs[0], 0)}
                    {buildBreadcrumbSeparator()}
                    {buildSingleBreadcrumb(breadcrumbs[1], 1)}
                    {buildBreadcrumbSeparator()}
                    <Text>
                        ...
                    </Text>
                    {buildBreadcrumbSeparator()}
                    {buildSingleBreadcrumb(breadcrumbs[breadcrumbs.length - 2], 2)}
                    {buildBreadcrumbSeparator()}
                    {buildSingleBreadcrumb(breadcrumbs[breadcrumbs.length - 1], 3)}
                </HStack>
            );
        }

        if (breadcrumbs.length === 1) {
            return (
                <Text fontSize="16px" fontWeight="bold" maxW={"50%"} overflowX={"hidden"} maxH={"40px"}>
                </Text>
            );
        }
        return (
            <HStack fontSize="16px" fontWeight="bold" spacing={0} maxW={"50%"} overflowX={"hidden"} maxH={"40px"}>
            {
                breadcrumbs?.map((folder, index) => (
                <HStack spacing={0}>
                    {buildSingleBreadcrumb(folder, index)}
                    {index < breadcrumbs.length - 1 && (
                        buildBreadcrumbSeparator()
                    )}
                </HStack>
                ))
            }
            </HStack>
        );
    }

    const addFolderToSession = (parentId, folder) => {
        const folderStruct = {
            id: folder.id,
            name: folder.name,
            parent_id: parentId,
        }
        const folderStructure = JSON.parse(sessionStorage.getItem("folderStructure")) || [];
        const existingFolderIndex = folderStructure.findIndex(f => f.id === folder.id);
        if (existingFolderIndex !== -1) {
            folderStructure[existingFolderIndex] = folderStruct;
        } else {
            folderStructure.push(folderStruct);
        }
        sessionStorage.setItem("folderStructure", JSON.stringify(folderStructure));
    }

    const openFolder = (folderId) => {
        if (folderId === undefined) {
            navigate(`/my-library`);
            return;
        }
        navigate(`/my-library/folder/${folderId}`);
    };

    const handleCreateFolder = ({newFolderName}) => {
        if (newFolderName.trim() === "") {
            alert("Folder name cannot be empty");
            return;
        }

        apiClient.post('/library/library-actions', {
            action: "upload_folder",
            upload_folder: {
                folder_name: newFolderName,
                parent_folder_id: finalFolderId,
            }
        }).then(({ data }) => {
            if (data.success) {
                setFolders([...folders, {
                    id: data.upload_folder.folder_id,
                    name: newFolderName,
                }]);
                addFolderToSession(finalFolderId, {
                    id: data.upload_folder.folder_id,
                    name: newFolderName,
                });
                onClose();
            } else {
                alert("Failed to create folder");
            }
        });
    };

    const handleUploadFile = async (file, accessType, groups, allTags) => {
        console.log("[FileLibrary] Upload File", file);
        let groupIds = [];
        if (accessType === "groups") {
            groupIds = groups.map(group => group.id);
        }

        const resp1 = await apiClient.post('/library/library-actions', {
            action: "upload_file",
            upload_file: {
                folder_id: finalFolderId,
            }
        })
        
        const data = resp1.data;
        console.log("[FileLibrary] Upload File Response", data);
        if (data.success) {
            const formData = file;

            const response = await fetch(data.upload_file.upload_url, {
                method: "PUT",
                body: formData,
            })

            console.log("[FileLibrary] Upload File Response", response);
            if (response.ok) {
                const resp2 = await apiClient.post('/library/library-actions', {
                    action: "complete_upload_file",
                    complete_upload_file: {
                        file_id: data.upload_file.file_id,
                        file_name: file.name,
                        share_with_org: accessType == "org" ? true : false,
                        keep_private: accessType == "private" ? true : false,
                        group_ids: accessType == "groups" ? groupIds : [],
                        tags : allTags?.length > 0 ? allTags.map(tag => {
                            return {
                                tag_key: tag.tag_key,
                                tag_value: tag.tag_value,
                            }
                        }) : [],
                    }
                })

                const data2 = resp2.data;
                if (data2.success) {
                    setFiles([...files, data2.complete_upload_file.file]);
                } else {
                    alert("Failed to complete file upload");
                }
            } else {
                alert("Failed to upload file");
            }
        }
    }

    if (loading) {
        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="50%"
            >
                <Text fontSize="2xl" fontWeight="bold">Loading...</Text>
            </Box>
        )
    }

    if (requiresSetup) {
        return (
            <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="center" 
                height="50%"
                flexDirection={"column"}
            >
                <Text fontSize="2xl" fontWeight="bold">Lets Start setting up library</Text>
                <Button 
                    margin={"24px"} 
                    color={"white"} 
                    bg={"#2584FD"} 
                    _hover={{
                        background: "#2584FD",
                        color: "white",
                    }}
                    onClick={() => {setupLibrary()}}
                >
                    Setup Library
                </Button>
            </Box>
        )
    }

    if (viewType === 'grid') {
        return <GridFileLibrary
            folders={folders}
            files={files}
            setFiles={setFiles}
            openFolder={openFolder}
            isOpen={isLibraryOpen}
            onClose={onClose}
            handleCreateFolder={handleCreateFolder}
            handleUploadFile={handleUploadFile}
            timeZone={timezone}
            handleTimezoneChange={handleTimezoneChange}
            isButtonDisabled={isButtonDisabled}
            getFolderBreadcrumbs={getFolderBreadcrumbs}
            onOpen={onOpen}
            groups={groups}
            getGroups={getGroups}
        />;
    }

    return <GridFileLibrary 
        folders={folders} 
        files={files} 
        setFiles={setFiles}
        openFolder={openFolder} 
        isOpen={isLibraryOpen} 
        onClose={onClose} 
        handleCreateFolder={handleCreateFolder}
        handleUploadFile={handleUploadFile}
        timeZone={timezone}
        handleTimezoneChange={handleTimezoneChange}
        isButtonDisabled={isButtonDisabled}
        getFolderBreadcrumbs={getFolderBreadcrumbs}
        onOpen={onOpen}
        groups={groups}
        getGroups={getGroups}
    />;
}

const GridFileLibrary = ({
    folders,
    files,
    setFiles,
    openFolder,
    isOpen,
    onClose,
    handleCreateFolder,
    handleUploadFile,
    timeZone,
    handleTimezoneChange,
    isButtonDisabled,
    getFolderBreadcrumbs,
    onOpen,
    groups,
    getGroups,
}) => {
    const [highlightedId, setHighlightedId] = React.useState("");
    const [sortDirection, setSortDirection] = React.useState("asc");
    const [sortColumn, setSortColumn] = React.useState("");
    const [isOpenAction, setIsOpenAction] = React.useState("");
    const [searchQuery, setSearchQuery] = React.useState("");
    const [fileToDisplay, setFileToDisplay] = React.useState(null);
    const location = useLocation();

    const displayGroupsForFile = (file) => {
        console.log("[FileLibrary] displayGroupsForFile", file);
        setFileToDisplay(file);
        setIsOpenAction("file-groups");
        onOpen();
    }   

    React.useEffect(() => {
        if (sortDirection === "asc") {
            const sortedFiles = [...files].sort((a, b) => {
                if (sortColumn === "name") {
                    return a.name.localeCompare(b.name);
                } else if (sortColumn === "size") {
                    return a.size - b.size;
                } else if (sortColumn === "owner") {
                    return a.owner.localeCompare(b.owner);
                } else if (sortColumn === "last_modified") {
                    return new Date(a.updated_at) - new Date(b.updated_at);
                }
                return 0;
            });
            setFiles(sortedFiles);
        } else {
            const sortedFiles = [...files].sort((a, b) => {
                if (sortColumn === "name") {
                    return b.name.localeCompare(a.name);
                } else if (sortColumn === "size") {
                    return b.size - a.size;
                } else if (sortColumn === "owner") {
                    return b.owner.localeCompare(a.owner);
                } else if (sortColumn === "last_modified") {
                    return new Date(b.updated_at) - new Date(a.updated_at);
                }
                return 0;
            });
            setFiles(sortedFiles);
        }
    }, [sortColumn, sortDirection]);

    if (folders.length === 0 && files.length === 0) {
        return (
            <Box
                display="flex"
                flexDirection={"column"}
                height="100%"
            >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    {getFolderBreadcrumbs()}
                    <HStack>
                        <TimezonePanel timezone={timeZone} handleTimezoneChange={handleTimezoneChange}/>
                        <FileLibraryTopButton onOpen={onOpen} setIsOpenAction={setIsOpenAction} isButtonDisabled={isButtonDisabled}/>
                    </HStack>
                </Box>
                <Box 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="center" 
                    height="50%"
                >
                    <Text fontSize="2xl" fontWeight="bold">No Files or Folders Found</Text>
                    <CreateFolderModal isOpen={isOpen} onClose={onClose} isOpenAction={isOpenAction} handleCreateFolder={handleCreateFolder}/>
                    <UploadFileModal isOpen={isOpen} onClose={onClose} isOpenAction={isOpenAction} handleUploadFile={handleUploadFile} groups={groups} getGroups={getGroups}/>
                </Box>
            </Box>
        )
    }

    return (
        <Flex direction="column" maxH={"90%"} overflow={"scroll"} data-scrollable="true" h={"90%"}>

            <Box display="flex" justifyContent="space-between" alignItems="center">
                {/* <InputGroup maxW="300px">
                    <InputLeftElement pointerEvents="none">
                        <IconButton style={{ color: 'grey', backgroundColor: "transparent" }} ><Search /></IconButton>
                    </InputLeftElement>
                    <Input
                        placeholder="Search a file or folder"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)} // Update search query
                        disabled={true}
                    />
                </InputGroup> */}
                {getFolderBreadcrumbs()}
                <HStack>
                    <TimezonePanel timezone={timeZone} handleTimezoneChange={handleTimezoneChange}/>
                    <FileLibraryTopButton onOpen={onOpen} setIsOpenAction={setIsOpenAction} isButtonDisabled={isButtonDisabled}/>
                </HStack>
            </Box>
            {
                folders.length > 0 && (
                    <Box>
                        <Text color={"#3d3d3d"} fontSize={"16px"} lineHeight={"24px"} marginTop={"12px"}>Folders</Text>
                        {/* Folder List */}
                        <Box display="flex" flexWrap="wrap" gap="16px" marginY={"16px"}>
                            {folders.map((folder) => (
                                <GridFileLibraryFolder folder={folder} highlightedId={highlightedId} setHighlightedId={setHighlightedId} openFolder={openFolder}/>
                            ))}
                        </Box>
                    </Box>
                )
            }
            {
                files.length > 0 && (
                    <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Text color={"#3d3d3d"} fontSize={"16px"} lineHeight={"24px"} marginTop={"12px"}>Files</Text>
                        </Box>
                        {/* File Table */}
                        <Box marginY={"16px"} overflowX="auto" maxH={"100%"}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        <th style={{ borderTop: "1px solid #ccc", borderBottom: "1px solid #ccc", padding: "8px", textAlign: "left" }}>
                                            File Name
                                            <ControlArrows columnName="name" sortColumn={sortColumn} setSortColumn={setSortColumn} sortDirection={sortDirection} setSortDirection={setSortDirection}/>
                                        </th>
                                        <th style={{ borderTop: "1px solid #ccc", borderBottom: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                                            Size
                                            <ControlArrows columnName="size" sortColumn={sortColumn} setSortColumn={setSortColumn} sortDirection={sortDirection} setSortDirection={setSortDirection}/>
                                        </th>
                                        <th style={{ borderTop: "1px solid #ccc", borderBottom: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                                            Owner
                                            <ControlArrows columnName="owner" sortColumn={sortColumn} setSortColumn={setSortColumn} sortDirection={sortDirection} setSortDirection={setSortDirection}/>
                                        </th>
                                        <th style={{ borderTop: "1px solid #ccc", borderBottom: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                                            Tags
                                        </th>
                                        <th style={{ borderTop: "1px solid #ccc", borderBottom: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                                            Last Modified At
                                            <ControlArrows columnName="last_modified" sortColumn={sortColumn} setSortColumn={setSortColumn} sortDirection={sortDirection} setSortDirection={setSortDirection}/>
                                        </th>
                                        <th style={{ borderTop: "1px solid #ccc", borderBottom: "1px solid #ccc", padding: "8px", textAlign: "left" }}>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody style={{ height: "10px", overflowY: "hidden" }}>
                                    {files.map((file) => {
                                        const formatSize = (size) => {
                                            if (size < 1024) return `${size} Bytes`;
                                            if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
                                            if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
                                            return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
                                        };

                                        return (
                                            <tr 
                                                key={file.id} 
                                                style={{ 
                                                    width: "100%", 
                                                    backgroundColor: highlightedId === file.id ? "#F4F4F4" : "transparent" 
                                                }}
                                                onMouseEnter={() => setHighlightedId(file.id)}
                                                onMouseLeave={() => setHighlightedId("")}
                                            >
                                                <td style={{ borderBottom: "1px solid #ccc", padding: "8px", maxWidth: "400px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    <HStack 
                                                        onClick={() => DownloadFile2(file.id)} 
                                                        _hover={{ cursor: "pointer", textDecoration: "underline dotted" }}
                                                    >
                                                        <FileIcon fileExtension={file?.content_type} />
                                                        <Text isTruncated>{file.name}</Text>
                                                    </HStack>
                                                </td>
                                                <td style={{ borderBottom: "1px solid #ccc", padding: "8px", textAlign: "center" }}>{file.size ? formatSize(file.size) : "N/A"}</td>
                                                <td style={{ borderBottom: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                                                    <HStack
                                                        spacing={2}
                                                        justify={"center"}
                                                        width={"100%"}
                                                    >
                                                        <Text
                                                            maxW={`calc(100% - 56px)`}
                                                        >
                                                            {file.owner ? file.owner : "N/A"}
                                                        </Text>
                                                        <Tooltip label={"Shared with Organisation"} 
                                                            placement='top'
                                                            isDisabled={!file.is_shared_with_org}
                                                            bg={"#8d8d8d"}
                                                            color={"#f2f2f2"}
                                                            fontWeight={500}
                                                            fontFamily="'Inter', sans-serif" 
                                                            offset={[0, -3]}
                                                        >
                                                            <IconButton
                                                                sx={{
                                                                    padding:0,
                                                                    bgColor: "transparent",
                                                                    "&:hover":{backgroundColor: "transparent"},
                                                                    width: "20px",
                                                                    height: "20px",
                                                                    minW:"unset",
                                                                    cursor: !file.is_shared_with_org ? "default" : "pointer",
                                                                }}
                                                                onClick={() => {file.is_shared_with_org && displayGroupsForFile(file)}}
                                                            >
                                                                <DomainOutlined
                                                                    sx={{ 
                                                                        padding:0, 
                                                                        fontSize: 20, 
                                                                        color: file.is_shared_with_org ? "#6d6d6d" : "#cdcdcd",
                                                                        bgColor: "transparent",
                                                                    }}
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip label={"Click to check shared groups"} 
                                                            placement='top'
                                                            isDisabled={file.shared_groups?.length == 0}
                                                            bg={"#8d8d8d"}
                                                            color={"#f2f2f2"}
                                                            fontWeight={500}
                                                            fontFamily="'Inter', sans-serif" 
                                                            offset={[0, -3]}
                                                        >
                                                            <IconButton
                                                                sx={{
                                                                    padding:0,
                                                                    bgColor: "transparent",
                                                                    "&:hover":{backgroundColor: "transparent"},
                                                                    width: "20px",
                                                                    height: "20px",
                                                                    minW:"unset",
                                                                    cursor: file.shared_groups?.length == 0 ? "default" : "pointer",
                                                                }}
                                                                onClick={() => {file.shared_groups?.length != 0 && displayGroupsForFile(file)}}
                                                            >
                                                                <GroupOutlined
                                                                    sx={{ 
                                                                        padding:0, 
                                                                        fontSize: 20, 
                                                                        color: file.shared_groups?.length == 0 ? "#cdcdcd" : "#6d6d6d",
                                                                        bgColor: "transparent",
                                                                    }}
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </HStack>
                                                </td>
                                                <td style={{ 
                                                    borderBottom: "1px solid #ccc",
                                                    padding: "8px",
                                                    textAlign: "center",
                                                    whiteSpace: "nowrap",
                                                    maxWidth: "200px",
                                                }}>
                                                    {file.tags?.length > 0 ? (
                                                        <HStack spacing={0} onClick={() => {displayGroupsForFile(file)}} _hover={{ cursor: "pointer" }}>
                                                            <Box
                                                                p="2"
                                                                border="1px solid #e5e5e5"
                                                                borderRadius="4px"
                                                                bg="#f9f9f9"
                                                                display="flex"
                                                                alignItems="center"
                                                                // maxW={"80%"}
                                                            >
                                                                <Text fontSize="14px" color="#1d1d1d">
                                                                    {file.tags[0].tag_key}: {file.tags[0].tag_value}
                                                                </Text>
                                                            </Box>
                                                            {file.tags.length > 1 && (
                                                                <Text fontSize="14px" color="#1d1d1d" ml="2">
                                                                    +{file.tags.length - 1} more
                                                                </Text>
                                                            )}
                                                        </HStack>
                                                    ) : ""}
                                                </td>
                                                <td style={{ borderBottom: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                                                    {file.updated_at 
                                                        ? new Date(file.updated_at).toLocaleString('en-GB', { timeZone: timeZone, hour12: true, hour: '2-digit', minute: '2-digit', second: '2-digit', day: '2-digit', month: '2-digit', year: '2-digit' })
                                                        : "N/A"}
                                                </td>
                                                <td style={{ borderBottom: "1px solid #ccc", padding: "8px" }}>
                                                    <Grid placeItems="center" h="100%">
                                                    <Menu placement="bottom" justify={"flex-end"} offset={[-0, -20]}>
                                                        <MenuButton
                                                        as={IconButton}
                                                        sx={{ paddingX: 0, paddingY: 0.5, backgroundColor: "transparent", "&:hover": { backgroundColor: "transparent" } }}
                                                        >
                                                        <MoreHoriz sx={{ padding: 0, fontSize: 20, color: "#A1B3D3", bgcolor: "transparent" }} />
                                                        </MenuButton>
                                                        <MenuList
                                                        borderRadius={"8px"}
                                                        paddingX={0}
                                                        paddingY={0}
                                                        width={"120px"}
                                                        marginTop={"8px"}
                                                        boxShadow={"0px 4px 8px rgba(0, 0, 0, 0.1)"}
                                                        backgroundColor={"#FFFFFF"}
                                                        border={"1px solid #E5E5E5"}
                                                        borderColor={"#E5E5E5"}
                                                        minW={"unset"}
                                                        >
                                                        <MenuItem
                                                            paddingX={4}
                                                            paddingY={2}
                                                            fontSize={"14px"}
                                                            fontWeight={"400"}
                                                            color={"#1D1D1D"}
                                                            fontFamily={"'Inter', sans-serif"}
                                                            _hover={{ backgroundColor: "#E4F0FF" }}
                                                            onClick={() => {displayGroupsForFile(file)}}
                                                        >
                                                            Edit
                                                        </MenuItem>
                                                        {/* <MenuItem
                                                            paddingX={4}
                                                            paddingY={2}
                                                            fontSize={"14px"}
                                                            fontWeight={"400"}
                                                            color={"#1D1D1D"}
                                                            fontFamily={"'Inter', sans-serif"}
                                                            _hover={{ backgroundColor: "#E4F0FF" }}</Menu>
                                                            // onClick={() => {openDeleteGroupModal(group)}}
                                                        >
                                                            Delete
                                                        </MenuItem> */}
                                                        </MenuList>
                                                    </Menu>
                                                    </Grid>
                                                </td>
                                            </tr>
                                        )})}
                                </tbody>
                            </table>
                        </Box>
                    </Box>
                )
            }
            <CreateFolderModal isOpen={isOpen} onClose={onClose} isOpenAction={isOpenAction} handleCreateFolder={handleCreateFolder}/>
            <UploadFileModal isOpen={isOpen} onClose={onClose} isOpenAction={isOpenAction} handleUploadFile={handleUploadFile} groups={groups} getGroups={getGroups}/>
            <FileGroupsModal isOpen={isOpen} onClose={onClose} isOpenAction={isOpenAction} fileToDisplay={fileToDisplay} setFileToDisplay={setFileToDisplay} groups={groups} getGroups={getGroups}/>

            {
                location.pathname === '/my-library' && false && (
                    <Box>
                        <Text color={"#3d3d3d"} fontSize={"16px"} lineHeight={"24px"} marginTop={"48px"}>Suggested folders</Text>
                        {/* Folder List */}
                        <Box display="flex" flexWrap="wrap" gap="16px" marginY={"16px"}>
                            <GridFileLibraryFolder folder={{
                                id: "a1",
                                name: "Won RFPs",
                            }} isDisabled={true}/>
                            <GridFileLibraryFolder folder={{
                                id: "a2",
                                name: "Proposals 2025",
                            }} isDisabled={true}/>
                            <GridFileLibraryFolder folder={{
                                id: "a3",
                                name: "Sales Content",
                            }} isDisabled={true}/>
                        </Box>
                    </Box>
                )
            }
        </Flex>
    );
};

const TimezonePanel = ({timezone, handleTimezoneChange}) => {
    return (
        <HStack>
          {/* <Text fontSize="sm" fontWeight="bold" mb={1}>
            Select Timezone
          </Text> */}
          <Box border="1px solid #ccc" borderRadius="md" p={1} bg="#f9f9f9">
            <select 
              value={timezone} 
              onChange={handleTimezoneChange} 
              style={{
                width: '100%',
                border: 'none',
                outline: 'none',
                backgroundColor: 'transparent',
                fontSize: '14px',
                padding: '4px',
              }}
            >
              {Intl.supportedValuesOf('timeZone').map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </Box>
        </HStack>
    )
}

const GridFileLibraryFolder = ({folder, highlightedId, setHighlightedId, openFolder, isDisabled = false}) => {
    const handleClick = () => {
        if (highlightedId === folder.id) {
            openFolder(folder.id, folder.name);
        } else {
            setHighlightedId(folder.id);
        }
    };

    return (
        <Box
            width={["100%", "48%", "30%"]}
            borderWidth="1px" 
            borderRadius="lg" 
            overflow="hidden" 
            userSelect="none"
            p="4" 
            display="flex" 
            alignItems="center" 
            justifyContent="space-between"
            cursor="pointer"
            borderColor={highlightedId == folder.id ? "blue.400" : ""}
            _hover={{ bg: "gray.100" }}
            onClick={!isDisabled ? handleClick: null}
        >
            <Box display="flex" alignItems="center">
                <Box 
                    as="span" 
                    fontSize="2xl" 
                    maxW={"100%"}
                    color="yellow.500" 
                    mr="4"
                >
                    <IconButton
                        sx={{padding:0, bgColor: "transparent", "&:hover":{backgroundColor: "transparent"}}}
                    >
                        <FolderOpenOutlined
                            sx={{ padding:0, fontSize: 24, color: "#5d5d5d", bgColor: "transparent" }}
                        />
                    </IconButton>
                </Box>
                <Text fontWeight="bold">{folder.name}</Text>
            </Box>
        </Box>
    );
}

export default FileLibrary;